import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const ENTITY_LABEL = {
  customer: "Customer",
  vehicle: "Vehicle",
  transporter: "Transporter",
  user: "User",
  dispatch: "Dispatch",
};

export default function AuditLog() {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("all"); // all | edits | deletions
  const [search, setSearch] = useState("");

  useEffect(() => { api.get("/audit-log").then((r) => setRows(r.data)); }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      const isDeletion = r.kind === "deletion";
      if (filter === "edits" && isDeletion) return false;
      if (filter === "deletions" && !isDeletion) return false;
      if (!q) return true;
      const hay = [r.reason, r.entity_label, r.entity_type, r.field, r.requested_by_name, r.approved_by_name, String(r.old_value ?? ""), String(r.new_value ?? "")].join(" ").toLowerCase();
      return hay.includes(q);
    });
  }, [rows, filter, search]);

  const deletionCount = rows.filter((r) => r.kind === "deletion").length;
  const editCount = rows.length - deletionCount;

  return (
    <div className="space-y-6" data-testid="audit-log-page">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900">Audit Log</h1>
        <p className="text-slate-500 text-sm mt-1">Approved dispatch edits and admin deletions across the system.</p>
      </div>

      <Tabs value={filter} onValueChange={setFilter}>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <TabsList>
            <TabsTrigger value="all" data-testid="audit-tab-all">All ({rows.length})</TabsTrigger>
            <TabsTrigger value="edits" data-testid="audit-tab-edits">Edits ({editCount})</TabsTrigger>
            <TabsTrigger value="deletions" data-testid="audit-tab-deletions">Deletions ({deletionCount})</TabsTrigger>
          </TabsList>
          <div className="min-w-[260px]">
            <Input placeholder="Search entity, reason, who…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="audit-search-input" />
          </div>
        </div>

        <TabsContent value={filter} className="pt-4">
          <div className="table-wrap">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b text-xs uppercase text-slate-600">
                <tr>{["When", "Action", "Entity", "Old / Snapshot", "New Value", "Reason", "Performed By"].map((h) => <th key={h} className="px-3 py-2.5 text-left font-semibold">{h}</th>)}</tr>
              </thead>
              <tbody>
                {filtered.map((r, i) => {
                  const isDeletion = r.kind === "deletion";
                  const entity = ENTITY_LABEL[r.entity_type] || (isDeletion ? "Record" : "Dispatch");
                  return (
                    <tr key={r.id} className="border-b hover:bg-slate-50 align-top" data-testid={`audit-row-${i}`}>
                      <td className="px-3 py-2 font-mono-num text-xs whitespace-nowrap">{new Date(r.approved_at).toLocaleString()}</td>
                      <td className="px-3 py-2">
                        {isDeletion
                          ? <Badge className="bg-rose-100 text-rose-800">Deleted</Badge>
                          : <Badge className="bg-blue-50 text-blue-700">{r.field}</Badge>}
                      </td>
                      <td className="px-3 py-2 text-xs">
                        <div className="font-semibold text-slate-700">{entity}</div>
                        <div className="text-slate-500">{isDeletion ? (r.entity_label || (r.dispatch_id || "").slice(0, 8)) : (r.dispatch_id || "").slice(0, 8)}</div>
                      </td>
                      <td className="px-3 py-2 text-xs text-slate-700 max-w-xs break-words">{String(r.old_value ?? "—")}</td>
                      <td className="px-3 py-2 text-xs text-emerald-700 font-medium">{isDeletion ? "—" : String(r.new_value ?? "—")}</td>
                      <td className="px-3 py-2 text-xs max-w-xs">{r.reason || "—"}</td>
                      <td className="px-3 py-2 text-xs">{r.approved_by_name || r.requested_by_name || "—"}</td>
                    </tr>
                  );
                })}
                {filtered.length === 0 && <tr><td colSpan="7" className="text-center py-8 text-slate-500">No audit entries.</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
